import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getRFQ, updateRFQ } from '../../../models/rfqModel';
import { getUsers } from '../../../models/userModel';
import { toast } from 'react-toastify';
import WysiwygEditor from '../../../components/common/WysiwygEditor';
import CustomDatePicker from '../../../components/common/CustomDatePicker';

function RFQEdit() {
  const navigate = useNavigate();
  const { rfqId } = useParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [rfq, setRFQ] = useState(null);
  const [users, setUsers] = useState([]);
  
  const [formData, setFormData] = useState({
    procurement_title: '',
    procurement_ref: '',
    procurement_closing: '',
    procurement_closing_time: '',
    procurement_attach: '0',
    procurement_items: [''],
    procurement_qty: [''],
    procurement_text: '',
    procurement_approval: []
  });

  useEffect(() => {
    fetchRFQ();
    fetchUsers();
  }, [rfqId]);

  const fetchRFQ = async () => {
    try {
      setLoading(true);
      const response = await getRFQ(rfqId);
      
      // Handle different response formats
      let rfqData = response;
      if (response.status === 1 && response.data) {
        rfqData = response.data;
      } else if (response.data) {
        rfqData = response.data;
      }
      
      if (!rfqData || (response.status === 0)) {
        throw new Error(response.message || 'Failed to load RFQ');
      }
      
      setRFQ(rfqData);
      
      // Parse items and quantities (they are PHP serialized arrays)
      let items = [''];
      let quantities = [''];
      
      try {
        if (rfqData.procurement_items) {
          // Handle PHP serialized data like 'a:1:{i:0;s:1:"A";}'
          const itemsStr = rfqData.procurement_items.toString();
          if (itemsStr.startsWith('a:') && itemsStr.includes(':{')) {
            // This is PHP serialized data - try to extract values
            const matches = itemsStr.match(/s:\d+:"([^"]*)"/g);
            if (matches && matches.length > 0) {
              items = matches.map(match => {
                const valueMatch = match.match(/s:\d+:"([^"]*)"/); 
                return valueMatch ? valueMatch[1] : '';
              }).filter(item => item.trim() !== '');
            } else {
              items = [''];
            }
          } else if (Array.isArray(rfqData.procurement_items)) {
            items = rfqData.procurement_items.filter(item => item && item.trim() !== '');
          } else {
            // Try JSON parse or comma separation as fallback
            try {
              const parsed = JSON.parse(itemsStr);
              items = Array.isArray(parsed) ? parsed.filter(item => item && item.trim() !== '') : [itemsStr];
            } catch {
              items = itemsStr.split(',').filter(item => item.trim() !== '');
            }
          }
        }
        
        if (rfqData.procurement_qty) {
          const qtyStr = rfqData.procurement_qty.toString();
          if (qtyStr.startsWith('a:') && qtyStr.includes(':{')) {
            // This is PHP serialized data
            const matches = qtyStr.match(/s:\d+:"([^"]*)"/g);
            if (matches && matches.length > 0) {
              quantities = matches.map(match => {
                const valueMatch = match.match(/s:\d+:"([^"]*)"/); 
                return valueMatch ? valueMatch[1] : '';
              }).filter(qty => qty.trim() !== '');
            } else {
              quantities = [''];
            }
          } else if (Array.isArray(rfqData.procurement_qty)) {
            quantities = rfqData.procurement_qty.filter(qty => qty && qty.toString().trim() !== '');
          } else {
            try {
              const parsed = JSON.parse(qtyStr);
              quantities = Array.isArray(parsed) ? parsed.filter(qty => qty && qty.toString().trim() !== '') : [qtyStr];
            } catch {
              quantities = qtyStr.split(',').filter(qty => qty.trim() !== '');
            }
          }
        }
      } catch (parseError) {
        console.error('Error parsing items/quantities:', parseError);
        items = [''];
        quantities = [''];
      }
      
      // Ensure arrays have at least one empty item
      if (items.length === 0) items = [''];
      if (quantities.length === 0) quantities = [''];
      
      // Parse approval array
      let approvalArray = [];
      if (rfqData.procurement_approval && rfqData.procurement_approval !== '0') {
        try {
          const parsed = JSON.parse(rfqData.procurement_approval);
          approvalArray = Array.isArray(parsed) ? parsed : [];
        } catch {
          approvalArray = [];
        }
      }
      
      setFormData({
        procurement_title: rfqData.procurement_title || '',
        procurement_ref: rfqData.procurement_ref || '',
        procurement_closing: rfqData.procurement_closing || '',
        procurement_closing_time: rfqData.procurement_closing_time || '',
        procurement_attach: rfqData.procurement_attach || '0',
        procurement_items: items,
        procurement_qty: quantities,
        procurement_text: rfqData.procurement_text || '',
        procurement_approval: approvalArray
      });
      
    } catch (err) {
      setError(err.message || 'Failed to load RFQ');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      // Ensure we always have an array
      if (Array.isArray(response)) {
        setUsers(response);
      } else if (response && Array.isArray(response.data)) {
        setUsers(response.data);
      } else if (response && response.users && Array.isArray(response.users)) {
        setUsers(response.users);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => {
      const newArray = prev[field].filter((_, i) => i !== index);
      // Always keep at least one empty item
      return {
        ...prev,
        [field]: newArray.length === 0 ? [''] : newArray
      };
    });
    
    // Also update quantities array to match
    if (field === 'procurement_items') {
      setFormData(prev => {
        const newQtyArray = prev.procurement_qty.filter((_, i) => i !== index);
        return {
          ...prev,
          procurement_qty: newQtyArray.length === 0 ? [''] : newQtyArray
        };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.procurement_title.trim()) {
      toast.error('Project Name is required');
      return;
    }
    
    if (!formData.procurement_closing) {
      toast.error('Closing date is required');
      return;
    }
    
    if (!formData.procurement_closing_time) {
      toast.error('Closing time is required');
      return;
    }

    try {
      setSaving(true);
      
      const updatePayload = {
        procurement_id: rfqId,
        procurement_title: formData.procurement_title,
        procurement_ref: formData.procurement_ref,
        procurement_closing: formData.procurement_closing,
        procurement_closing_time: formData.procurement_closing_time,
        procurement_attach: formData.procurement_attach,
        procurement_items: formData.procurement_items.filter(item => item.trim() !== ''),
        procurement_qty: formData.procurement_qty.filter(qty => qty.trim() !== ''),
        procurement_text: formData.procurement_text,
        procurement_approval: formData.procurement_approval,
        procurement_user: user?.user_id || rfq?.procurement_user,
        procurement_spec: rfq?.procurement_spec || ''
      };
      
      const response = await updateRFQ(rfqId, updatePayload);
      
      if (response && (response.status === 'success' || response.status === 1)) {
        toast.success('RFQ updated successfully');
        navigate(`/dashboard/rfqs/${rfqId}`);
      } else {
        toast.error(response?.message || 'Failed to update RFQ');
      }
      
    } catch (err) {
      console.error('Error updating RFQ:', err);
      toast.error(err.message || 'Failed to update RFQ');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading RFQ...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard/rfqs')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to RFQs
          </button>
        </div>
        
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-xl mb-2">Error Loading RFQ</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/dashboard/rfqs')}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            >
              Go to RFQs
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/dashboard/rfqs/${rfqId}`)}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to RFQ
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900">Edit RFQ</h1>
        <p className="text-gray-600 mt-1">
          Update RFQ: {rfq?.procurement_title}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Form */}
          <div className="flex-1 lg:w-2/3 space-y-6">
            
            {/* RFQ Basic Information */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">RFQ Details</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.procurement_title}
                  onChange={(e) => handleInputChange('procurement_title', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Items and Quantities */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Items and Quantities</h2>
              
              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-3 text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
                  <div className="col-span-8">Item</div>
                  <div className="col-span-2">Quantity</div>
                  <div className="col-span-2">Action</div>
                </div>
                
                {formData.procurement_items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3">
                    <div className="col-span-8">
                      <input
                        type="text"
                        placeholder="Item description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        value={item}
                        onChange={(e) => handleArrayChange('procurement_items', index, e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        placeholder="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        value={formData.procurement_qty[index] || ''}
                        onChange={(e) => handleArrayChange('procurement_qty', index, e.target.value)}
                      />
                    </div>
                    <div className="col-span-2 flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeArrayItem('procurement_items', index)}
                        className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={() => {
                    addArrayItem('procurement_items');
                    addArrayItem('procurement_qty');
                  }}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Item
                </button>
              </div>
            </div>

            {/* Detailed Specifications */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Detailed Specifications</h2>
              <WysiwygEditor
                value={formData.procurement_text}
                onChange={(value) => handleInputChange('procurement_text', value)}
                placeholder="Enter detailed specifications..."
              />
            </div>
            
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/3 space-y-6">
            
            {/* Date and Time Settings */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Date & Time Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Closing Date *
                  </label>
                  <CustomDatePicker
                    value={formData.procurement_closing}
                    onChange={(value) => handleInputChange('procurement_closing', value)}
                    placeholder="Select closing date"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Closing Time *
                  </label>
                  <input
                    type="time"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.procurement_closing_time}
                    onChange={(e) => handleInputChange('procurement_closing_time', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Additional Options */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Options</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.procurement_ref}
                    onChange={(e) => handleInputChange('procurement_ref', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Requires Attachments
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.procurement_attach}
                    onChange={(e) => handleInputChange('procurement_attach', e.target.value)}
                  >
                    <option value="0">No</option>
                    <option value="1">Yes</option>
                  </select>
                </div>
              </div>
            </div>



            {/* Action Buttons */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/rfqs/${rfqId}`)}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
            
          </div>
        </div>
      </form>
    </div>
  );
}

export default RFQEdit;